import { DEFAULT_LOCALE, LOCALES, type SupportedLocale } from "@/lib/i18n";

/** Dynamic route params that must never appear in the browser URL query string. */
export const INTERNAL_ROUTE_QUERY_KEYS = [
  "locale",
  "productsSlug",
  "category",
  "subcategory",
  "id",
] as const;

export function stripInternalRouteQuery(search: string): string {
  const raw = search.replace(/^\?/, "");
  if (!raw) return "";
  const params = new URLSearchParams(raw);
  for (const key of INTERNAL_ROUTE_QUERY_KEYS) {
    params.delete(key);
  }
  const cleaned = params.toString();
  return cleaned ? `?${cleaned}` : "";
}

/** Strip internal `/en` rewrite prefix and leaked dynamic-route query params.
 *
 * Wyjątek: strony produktu `/en/{slug}-n{numer}` zachowują prefiks `/en/`,
 * bo to ich jedyna publiczna ścieżka rozumiana przez client-side Pages Router
 * (`/[locale]/[productsSlug]`). Bez prefiksu klient dostaje invariant
 * „attempted to hard navigate to the same URL”.
 */
export function getPublicPathFromAsPath(asPath: string): string {
  const [pathnamePart, search = ""] = (asPath || "/").split("?");
  const trimmed = pathnamePart.replace(/\/+$/, "") || "/";
  const segments = trimmed.replace(/^\/+/, "").split("/").filter(Boolean);

  let publicPathname: string;
  if (segments[0] === DEFAULT_LOCALE) {
    const rest = segments.slice(1);
    const isProductSubpath = rest.length === 1 && /-n\d+$/.test(rest[0] || "");
    if (isProductSubpath) {
      publicPathname = trimmed;
    } else {
      const restJoined = rest.join("/");
      publicPathname = restJoined ? `/${restJoined}` : "/";
    }
  } else {
    publicPathname = trimmed;
  }

  return publicPathname + stripInternalRouteQuery(search);
}

/** Pathname only (no query), with `/en` rewrite prefix removed for English. */
export function getPublicPathname(pathOrAsPath: string): string {
  return getPublicPathFromAsPath(pathOrAsPath).split("?")[0] || "/";
}

/** Same as Layout `pathWithoutLocalePrefix` — for hreflang / locale switching. */
export function pathWithoutLocalePrefix(path: string): string {
  const segments = path.replace(/^\/+/, "").split("/").filter(Boolean);
  if (LOCALES.includes(segments[0] as SupportedLocale)) {
    const rest = segments.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }
  return path || "/";
}
